import { ReactElement, useEffect, useState } from 'react';
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';

type SidebaarIconsProperties = {
  icondetails: {
    iconName: string;
    iconText: string;
    /** Tab of the parent route, for entries that are views of the parent page. */
    tab?: string;
    /** Standalone route, for entries that are their own page nested under the parent. */
    route?: string;
  };
  parentRoute: string;
};

/**
 * SidebarIcon component displaying an icon and showing the icon name on hover.
 *
 * A sub-entry is either a tab of the parent page (`tab`, linked as
 * `/parent?tab=x`) or a page of its own grouped under the parent (`route`,
 * linked and highlighted by pathname).
 * @param  props - The properties for the SidebarIcon component.
 * @returns - SidebarIcon component.
 */
export default function SidebarSubIcons(props: SidebaarIconsProperties): ReactElement {
  const {
    icondetails: { iconName, iconText, tab, route },
    parentRoute
  } = props;
  const [isActiveLink, setIsActiveLink] = useState(false);
  const [params] = useSearchParams();
  const currentTab = params.get('tab');
  const { pathname } = useLocation();

  useEffect(() => {
    setIsActiveLink(route ? pathname === `/${route}` : currentTab === tab);
  }, [currentTab, pathname, route, tab]);

  return (
    <NavLink to={route ? `/${route}` : `/${parentRoute}?tab=${tab}`}>
      <div className="text-gray-500 my-2 group/link">
        <div
          className={clsx('rounded cursor-pointer p-1.5 flex items-center w-full justify-between', {
            'text-gray-700 dark:text-gray-300 bg-primary-50 dark:bg-background-500': isActiveLink,
            'group-hover/link:bg-primary-50 dark:group-hover/link:bg-background-500 dark:bg-background-700 text-gray-400 dark:text-gray-500 dark:group-hover/link:text-gray-300 group-hover/link:text-gray-700':
              !isActiveLink
          })}
        >
          <div className="flex items-center">
            <span
              className={clsx(
                {
                  'material-icons': isActiveLink,
                  'material-icons-outlined': !isActiveLink,
                  'text-primary-500': isActiveLink
                },
                'text-xl'
              )}
            >
              {iconName}
            </span>
            <p className="text-base font-medium pl-3 hidden group-hover/sidenav:block group-hover/sidenav">
              <span>{iconText}</span>
            </p>
          </div>
        </div>
      </div>
    </NavLink>
  );
}
